import { createContext, ReactNode, useContext, useRef } from 'react';
import { Toast } from 'primereact/toast';

interface ToastContextType {
  showSuccess: (summary: string, detail?: string) => void;
  showError: (summary: string, detail?: string) => void;
  showInfo: (summary: string, detail?: string) => void;
  showWarn: (summary: string, detail?: string) => void;
}
const toastLife = 3000;
// Errors may list several fields, so they need noticeably longer than a success note.
const errorToastLife = 10000;
const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({
  children,
  stickyErrors = false,
}: {
  children: ReactNode;
  /** Error toasts stay until closed and use a larger, emphasised style. */
  stickyErrors?: boolean;
}) {
  const toast = useRef<Toast>(null);

  const showSuccess = (summary: string, detail?: string) => {
    toast.current?.show({
      severity: 'success',
      summary,
      detail,
      life: toastLife,
    });
  };

  const showError = (summary: string, detail?: string) => {
    toast.current?.show({
      severity: 'error',
      summary,
      detail,
      life: errorToastLife,
      sticky: stickyErrors,
      className: stickyErrors ? 'poem-toast-error-prominent' : undefined,
    });
  };

  const showInfo = (summary: string, detail?: string) => {
    toast.current?.show({ severity: 'info', summary, detail, life: toastLife });
  };

  const showWarn = (summary: string, detail?: string) => {
    toast.current?.show({ severity: 'warn', summary, detail, life: toastLife });
  };

  return (
    <ToastContext.Provider
      value={{ showSuccess, showError, showInfo, showWarn }}
    >
      <Toast ref={toast} />
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}
